import { supabase } from '@/lib/supabase';
import { Plan } from '@/features/plans/types';
import type { Database } from '@/lib/database.types';

// Define channel types
type BaseChannel = {
  id: number;
  name: string;
};

type VersionedChannel = BaseChannel & {
  version_id: number;
};

type PreviousVersionResponse = {
  channel: BaseChannel | null;
};

type PlanResponse = Database['public']['Tables']['plans']['Row'] & {
  channel_plans: Array<{
    channel: VersionedChannel | null;
  }>;
  version?: {
    id: number;
    status: string;
  };
  features: any[];
};

type PreviousChannelResponse = {
  channel: {
    id: number;
    name: string;
    version_id: number;
  };
  plan_id: number;
};

// Add at the top with other type definitions
type VersionAncestry = {
  version_id: number;
  depth: number;
};

export const planQueries = {
  getPlans: async (providerId: number, versionId: number): Promise<Plan[]> => {
    const { data: versionData, error: versionError } = await supabase
      .from('provider_versions')
      .select(`
        id,
        provider_id,
        version_number,
        status,
        parent_version_id,
        source_version_id
      `)
      .eq('id', versionId)
      .single();

    if (versionError) {
      throw versionError;
    }

    const isDraft = versionData?.status === 'draft';
    const parentVersionId = versionData?.parent_version_id;
    
    // Add error handling for version ancestry
    const { data: versionAncestry, error: ancestryError } = await supabase
      .rpc('get_version_ancestry', { 
        p_version_id: versionId 
      });

    // Declare ancestorVersionIds in the outer scope
    let ancestorVersionIds: number[];

    if (ancestryError) {
      console.warn('Failed to get version ancestry:', ancestryError);
      // For first version or error, just use current version
      ancestorVersionIds = [versionId];
    } else {
      // Handle both first version and version chain cases
      ancestorVersionIds = versionAncestry?.map((v: VersionAncestry) => v.version_id) || [versionId];
      console.log('Version ancestry:', ancestorVersionIds);
    }

    // Update version changes query to handle text entity_id
    const { data: versionChanges } = await supabase
      .from('change_log')
      .select('*')
      .eq('entity_type', 'channel_plans')
      .in('version_id', ancestorVersionIds)
      .returns<{
        entity_id: string;
        change_type: 'CREATE' | 'DELETE';
        version_id: number;
      }[]>();

    // Create a Set of removed channel-plan relationships using composite keys
    const removedCompositeIds = new Set(
      versionChanges
        ?.filter(vc => vc.change_type === 'DELETE')
        .map(vc => vc.entity_id)
    );

    // Get all plans with their current channel relationships
    const { data, error } = await supabase
      .from('plans')
      .select(`
        *,
        channel_plans!left(
          channel:channels!left(
            id,
            name,
            version_id
          )
        ),
        version:provider_versions!plans_version_id_fkey(
          id,
          status
        )
      `)
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .order('name');

    if (error) throw error;

    const plans = data as unknown as PlanResponse[];
        // If in draft, get previous version's channel relationships

    // If in draft, get previous version's channel relationships
    let previousVersionChannels: PreviousChannelResponse[] = [];
    if (isDraft && parentVersionId) {
      const { data: prevChannels } = await supabase
        .from('channel_plans')
        .select(`
          channel:channels!inner(
            id,
            name,
            version_id
          ),
          plan_id
        `)
        .eq('version_id', parentVersionId);

      previousVersionChannels = (prevChannels || []) as unknown as PreviousChannelResponse[];
    }

    // Transform plans with proper channel relationships
    const transformedPlans = await Promise.all(plans.map(async plan => {
      const currentChannels = (plan.channel_plans || [])
        .filter((cp): cp is { channel: NonNullable<VersionedChannel> } => 
          cp.channel !== null
        )
        .map(cp => ({
          id: cp.channel.id,
          name: cp.channel.name
        }));

      // Only inherit channels that aren't removed in any ancestor version
      const inheritedChannels = isDraft ?
        previousVersionChannels
          .filter((cp: PreviousChannelResponse) => {
            if (!cp.channel) return false;
            const channel = cp.channel;
            const compositeId = `${channel.id}_${plan.id}`;
            return !currentChannels.some(cc => cc.id === channel.id) && 
                   !removedCompositeIds.has(compositeId);
          })
          .map(cp => {
            const channel = cp.channel!;
            return {
              id: channel.id,
              name: channel.name
            };
          }) :
        [];

      const allChannels = [...currentChannels, ...inheritedChannels];

      return {
        ...plan,
        channel_count: allChannels.length,
        channels: allChannels,
        description: plan.description || null,
        features: plan.features || []
      } as Plan;
    }));

    return transformedPlans;
  },

  getPlanById: async (id: number): Promise<Plan> => {
    const { data, error } = await supabase
      .from('plans')
      .select(`
        *,
        channel_plans!left(
          channel:channels!left(
            id,
            name
          )
        ),
        version:provider_versions!plans_version_id_fkey(
          id,
          status
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    const plan = data as unknown as PlanResponse;
    const isDraft = plan.version?.status === 'draft';
    let previousVersionChannels: PreviousVersionResponse[] = [];

    if (isDraft) {
      const { data: prevData } = await supabase
        .from('provider_versions')
        .select('id')
        .eq('provider_id', plan.provider_id || 0)
        .eq('status', 'published')
        .order('version_number', { ascending: false })
        .limit(1)
        .single();

      if (prevData) {
        // Check for explicit removals
        const { data: changes } = await supabase
          .from('change_log')
          .select('*')
          .eq('version_id', plan.version?.id || 0)
          .eq('entity_type', 'channel_plans')
          .eq('action_type', 'DELETE')
          .contains('old_value', { plan_id: id });

        const wasExplicitlyRemoved = changes && changes.length > 0;

        if (!wasExplicitlyRemoved) {
          const { data: prevChannels } = await supabase
            .from('channel_plans')
            .select(`
              channel:channels(
                id,
                name
              )
            `)
            .eq('version_id', prevData.id)
            .eq('plan_id', id);

          previousVersionChannels = (prevChannels || []) as unknown as PreviousVersionResponse[];
        }
      }
    }

    const currentChannels = (plan.channel_plans || [])
      .filter((cp): cp is { channel: NonNullable<VersionedChannel> } => 
        cp.channel !== null
      )
      .map(cp => ({
        id: cp.channel.id,
        name: cp.channel.name
      }));

    // Only inherit channels that aren't already in current version
    const inheritedChannels = isDraft ?
      previousVersionChannels
        .filter((cp: PreviousVersionResponse) => {
          // First check if channel exists
          if (!cp.channel) return false;
          const channel = cp.channel;
          return !currentChannels.some(cc => cc.id === channel.id);
        })
        .map(cp => {
          // TypeScript knows cp.channel is not null due to filter
          const channel = cp.channel!;
          return {
            id: channel.id,
            name: channel.name
          };
        }) :
      [];

    const allChannels = [...currentChannels, ...inheritedChannels];

    return {
      ...plan,
      channel_count: allChannels.length,
      channels: allChannels,
      description: plan.description || null,
      features: plan.features || []
    } as Plan;
  },

  getAvailableColors: async (providerId: number, versionId: number) => {
    const { data, error } = await supabase
      .from('plans')
      .select('id, color_index')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .order('id', { ascending: true });

    if (error) throw error;

    // Instead of returning unused colors, we'll now return colors in order of plan ID
    // which provides a consistent sequence across the app

    // Get all colors that are already assigned to existing plans
    const usedColors = new Map(data.map(p => [p.id, p.color_index]));
    
    // Create array of all available color indices (1-30)
    const allColorIndices = Array.from({ length: 30 }, (_, i) => i + 1);
    
    // Return all color indices that aren't currently in use
    // This maintains compatibility with existing code while ensuring we use sequential colors
    return allColorIndices.filter(colorIndex => !Array.from(usedColors.values()).includes(colorIndex));
  },

  async getTotalChannels(providerId: number, versionId: number) {
    const { data, error } = await supabase
      .from('channels')
      .select('id')
      .eq('provider_id', providerId)
      .eq('version_id', versionId);

    if (error) throw error;
    return data.length;
  }
};
